/**
 * System Health Page
 *
 * Displays system health status, memory stats, errors, and alerts.
 */

import { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../api/client';
import type {
  MemoryStatsSummary,
  SystemHealthSummary,
  SystemHealthSnapshot,
  ErrorSummary,
  AlertSummary,
  Alert,
} from '../api/types';
import { Card, CardContent, CardHeader, CardTitle, StatCard } from '../components/Card';
import { Badge } from '../components/Table';
import { Button } from '../components/Button';
import {
  Activity,
  Database,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Bell,
  Bug,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'success' | 'warning' | 'danger' | 'gray'> = {
    healthy: 'success',
    connected: 'success',
    degraded: 'warning',
    slow: 'warning',
    unhealthy: 'danger',
    error: 'danger',
    disconnected: 'danger',
  };

  const icons: Record<string, React.ReactNode> = {
    healthy: <CheckCircle className="w-3 h-3" />,
    connected: <CheckCircle className="w-3 h-3" />,
    degraded: <AlertTriangle className="w-3 h-3" />,
    slow: <Clock className="w-3 h-3" />,
    unhealthy: <XCircle className="w-3 h-3" />,
    error: <XCircle className="w-3 h-3" />,
    disconnected: <XCircle className="w-3 h-3" />,
  };

  return (
    <Badge variant={variants[status] ?? 'gray'} className="inline-flex items-center gap-1">
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

// Severity badge component
function SeverityBadge({ severity }: { severity: string }) {
  const variants: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'gray'> = {
    critical: 'danger',
    warning: 'warning',
    info: 'info',
  };
  return <Badge variant={variants[severity] ?? 'gray'}>{severity}</Badge>;
}

// Trend indicator
function TrendIndicator({ value }: { value: number }) {
  if (value > 0) {
    return (
      <span className="inline-flex items-center text-green-600 text-sm">
        <TrendingUp className="w-4 h-4 mr-1" />
        +{value}
      </span>
    );
  } else if (value < 0) {
    return (
      <span className="inline-flex items-center text-red-600 text-sm">
        <TrendingDown className="w-4 h-4 mr-1" />
        {value}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-gray-500 text-sm">
      <Minus className="w-4 h-4 mr-1" />
      0
    </span>
  );
}

export function SystemHealthPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [memoryStats, setMemoryStats] = useState<MemoryStatsSummary | null>(null);
  const [healthSummary, setHealthSummary] = useState<SystemHealthSummary | null>(null);
  const [currentHealth, setCurrentHealth] = useState<SystemHealthSnapshot | null>(null);
  const [errorSummary, setErrorSummary] = useState<ErrorSummary | null>(null);
  const [alertSummary, setAlertSummary] = useState<AlertSummary | null>(null);
  const [activeAlerts, setActiveAlerts] = useState<Alert[]>([]);

  const loadData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setIsRefreshing(true);

    try {
      const [memRes, healthRes, errRes, alertRes, alertsRes] = await Promise.all([
        apiClient.getMemoryStatsSummary().catch(() => null),
        apiClient.getSystemHealthSummary().catch(() => null),
        apiClient.getErrorSummary().catch(() => null),
        apiClient.getAlertSummary().catch(() => null),
        apiClient.getAlerts({ resolved: false, limit: 10 }).catch(() => null),
      ]);

      if (memRes?.success) setMemoryStats(memRes.summary);
      if (healthRes?.success) setHealthSummary(healthRes.summary);
      if (errRes?.success) setErrorSummary(errRes.summary);
      if (alertRes?.success) setAlertSummary(alertRes.summary);
      if (alertsRes?.success) setActiveAlerts(alertsRes.alerts);
    } catch (err) {
      console.error('Failed to load health data:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(() => loadData(), 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRefresh = () => {
    loadData(true);
  };

  const handleCheckHealth = async () => {
    setIsRefreshing(true);
    try {
      const res = await apiClient.checkSystemHealth();
      if (res.success) {
        setCurrentHealth(res.health);
      }
    } catch (err) {
      console.error('Failed to check health:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDetectProblems = async () => {
    setIsRefreshing(true);
    try {
      const res = await apiClient.detectProblems();
      if (res.success && res.alertsCreated > 0) {
        // Refresh data to show new alerts
        await loadData();
      }
    } catch (err) {
      console.error('Failed to detect problems:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await apiClient.acknowledgeAlert(alertId);
      loadData();
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      await apiClient.resolveAlert(alertId);
      loadData();
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
          <p className="text-gray-500 mt-1">Monitor system status, errors, and alerts</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="secondary" onClick={handleDetectProblems} disabled={isRefreshing}>
            <AlertTriangle className="w-4 h-4 mr-2" />
            Detect Problems
          </Button>
          <Button onClick={handleCheckHealth} disabled={isRefreshing}>
            <Activity className="w-4 h-4 mr-2" />
            Check Now
          </Button>
        </div>
      </div>

      {/* System Status Banner */}
      {healthSummary && (
        <div className={`rounded-xl p-4 ${
          healthSummary.currentStatus === 'healthy' ? 'bg-green-50 border border-green-200' :
          healthSummary.currentStatus === 'degraded' ? 'bg-yellow-50 border border-yellow-200' :
          'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <StatusBadge status={healthSummary.currentStatus} />
              <span className="text-gray-700">
                Uptime: <strong>{healthSummary.uptimePercent.toFixed(1)}%</strong> (24h)
              </span>
              <span className="text-gray-500">|</span>
              <span className="text-gray-700">
                Neo4j: <strong>{healthSummary.avgNeo4jLatencyMs.toFixed(0)}ms</strong>
              </span>
              <span className="text-gray-700">
                Qdrant: <strong>{healthSummary.avgQdrantLatencyMs.toFixed(0)}ms</strong>
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              {healthSummary.recentErrors > 0 && (
                <span className="text-red-600 flex items-center gap-1">
                  <Bug className="w-4 h-4" />
                  {healthSummary.recentErrors} recent errors
                </span>
              )}
              {healthSummary.activeAlerts > 0 && (
                <span className="text-yellow-600 flex items-center gap-1">
                  <Bell className="w-4 h-4" />
                  {healthSummary.activeAlerts} active alerts
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current Health Check Result */}
      {currentHealth && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Live Health Check
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="text-sm text-gray-500">Neo4j</div>
                <div className="flex items-center justify-between">
                  <StatusBadge status={currentHealth.neo4jStatus} />
                  {currentHealth.neo4jLatencyMs && (
                    <span className="text-sm text-gray-600">{currentHealth.neo4jLatencyMs}ms</span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-gray-500">Qdrant</div>
                <div className="flex items-center justify-between">
                  <StatusBadge status={currentHealth.qdrantStatus} />
                  {currentHealth.qdrantLatencyMs && (
                    <span className="text-sm text-gray-600">{currentHealth.qdrantLatencyMs}ms</span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-gray-500">PostgreSQL</div>
                <div className="flex items-center justify-between">
                  <StatusBadge status={currentHealth.postgresStatus} />
                  {currentHealth.postgresLatencyMs && (
                    <span className="text-sm text-gray-600">{currentHealth.postgresLatencyMs}ms</span>
                  )}
                </div>
              </div>
            </div>
            {currentHealth.memoryUsageMb && (
              <div className="mt-4 pt-4 border-t">
                <span className="text-sm text-gray-500">Memory Usage: </span>
                <span className="text-sm font-medium">{currentHealth.memoryUsageMb.toFixed(1)} MB</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Memory Stats */}
      {memoryStats && (
        <>
          <h2 className="text-lg font-semibold text-gray-900 mt-8">Memory Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Entities"
              value={memoryStats.totalEntities.toLocaleString()}
              subtitle={<TrendIndicator value={memoryStats.entityGrowth} />}
              icon={<Database className="w-6 h-6" />}
            />
            <StatCard
              title="Total Facts"
              value={memoryStats.totalFacts.toLocaleString()}
              subtitle={<TrendIndicator value={memoryStats.factGrowth} />}
              icon={<Database className="w-6 h-6" />}
            />
            <StatCard
              title="Total Episodes"
              value={memoryStats.totalEpisodes.toLocaleString()}
              subtitle={<TrendIndicator value={memoryStats.episodeGrowth} />}
              icon={<Database className="w-6 h-6" />}
            />
            <StatCard
              title="Active Facts"
              value={memoryStats.activeFacts.toLocaleString()}
              subtitle={`${memoryStats.invalidatedFacts} invalidated`}
              icon={<CheckCircle className="w-6 h-6" />}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              title="Today's Ingestions"
              value={memoryStats.todayIngestions.toLocaleString()}
              icon={<TrendingUp className="w-6 h-6" />}
            />
            <StatCard
              title="Today's Searches"
              value={memoryStats.todaySearches.toLocaleString()}
              icon={<Activity className="w-6 h-6" />}
            />
            <StatCard
              title="Today's Errors"
              value={memoryStats.todayErrors.toLocaleString()}
              icon={<Bug className="w-6 h-6" />}
            />
          </div>
        </>
      )}

      {/* Errors & Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Error Summary */}
        {errorSummary && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-red-600" />
                Errors (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Errors</span>
                <span className="font-semibold">{errorSummary.totalErrors}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Unresolved</span>
                <span className="font-semibold text-red-600">{errorSummary.unresolvedErrors}</span>
              </div>

              {Object.keys(errorSummary.byType).length > 0 && (
                <div className="pt-4 border-t">
                  <div className="text-sm text-gray-500 mb-2">By Type</div>
                  <div className="space-y-1">
                    {Object.entries(errorSummary.byType).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{type.replace('_', ' ')}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {errorSummary.recentErrors.length > 0 && (
                <div className="pt-4 border-t">
                  <div className="text-sm text-gray-500 mb-2">Recent Errors</div>
                  <div className="space-y-2">
                    {errorSummary.recentErrors.slice(0, 5).map((err) => (
                      <div key={err.id} className="text-sm p-2 bg-gray-50 rounded">
                        <div className="flex items-center justify-between">
                          <Badge variant="danger">{err.errorType.replace('_', ' ')}</Badge>
                          <span className="text-gray-400 text-xs">
                            {new Date(err.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-600 mt-1 truncate">{err.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Alert Summary */}
        {alertSummary && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-yellow-600" />
                Alerts (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Alerts</span>
                <span className="font-semibold">{alertSummary.totalAlerts}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Active</span>
                <span className="font-semibold text-yellow-600">{alertSummary.activeAlerts}</span>
              </div>

              {Object.keys(alertSummary.bySeverity).length > 0 && (
                <div className="pt-4 border-t">
                  <div className="text-sm text-gray-500 mb-2">By Severity</div>
                  <div className="flex gap-4">
                    {Object.entries(alertSummary.bySeverity).map(([severity, count]) => (
                      <div key={severity} className="flex items-center gap-2">
                        <SeverityBadge severity={severity} />
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Active Alerts */}
      {activeAlerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Active Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {activeAlerts.map((alert) => (
                <div key={alert.id} className="px-6 py-4 flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <SeverityBadge severity={alert.severity} />
                      <span className="text-sm text-gray-500">
                        {alert.alertType.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(alert.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-900">{alert.message}</p>
                    {alert.acknowledged && (
                      <span className="text-xs text-gray-500 mt-1 inline-block">
                        Acknowledged
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    {!alert.acknowledged && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleAcknowledgeAlert(alert.id)}
                      >
                        Acknowledge
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleResolveAlert(alert.id)}
                    >
                      Resolve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
